import type { AnalyticsDateRangePreset } from '@wooconnect/shared';

export function localDate(d: Date): string {
  const Y = d.getFullYear();
  const M = String(d.getMonth() + 1).padStart(2, '0');
  const D = String(d.getDate()).padStart(2, '0');
  return `${Y}-${M}-${D}`;
}

/**
 * Get "now" in a specific IANA timezone using Intl.DateTimeFormat.
 * Returns a Date whose local fields (getFullYear, getMonth, etc.) represent
 * the current wall-clock time in that timezone.
 */
function getNowInTimezone(timezone?: string): Date {
  if (!timezone) return new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((p) => p.type === type)?.value ?? 0);
    return new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  } catch {
    return new Date();
  }
}

export function computeDateRange(
  preset: AnalyticsDateRangePreset,
  weekStartsOn: 0 | 1 = 1,
  timezone?: string,
): { after: string; before: string; interval: 'day' | 'week' | 'month' } {
  const now = getNowInTimezone(timezone);
  const todayStr = localDate(now);
  const endOfToday = `${todayStr}T23:59:59`;
  const startOfToday = `${todayStr}T00:00:00`;

  switch (preset) {
    case 'today': {
      return { after: startOfToday, before: endOfToday, interval: 'day' };
    }
    case 'yesterday': {
      const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      const yStr = localDate(y);
      return { after: `${yStr}T00:00:00`, before: `${yStr}T23:59:59`, interval: 'day' };
    }
    case 'last7days': {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      return { after: `${localDate(d)}T00:00:00`, before: endOfToday, interval: 'day' };
    }
    case 'last14days': {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 13);
      return { after: `${localDate(d)}T00:00:00`, before: endOfToday, interval: 'day' };
    }
    case 'last30days': {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
      return { after: `${localDate(d)}T00:00:00`, before: endOfToday, interval: 'day' };
    }
    case 'last90days': {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 89);
      return { after: `${localDate(d)}T00:00:00`, before: endOfToday, interval: 'week' };
    }
    case 'last365days': {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 364);
      return { after: `${localDate(d)}T00:00:00`, before: endOfToday, interval: 'month' };
    }
    case 'thisWeek': {
      const day = now.getDay();
      const diff = (day - weekStartsOn + 7) % 7;
      const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
      return { after: `${localDate(weekStart)}T00:00:00`, before: endOfToday, interval: 'day' };
    }
    case 'lastWeek': {
      const day = now.getDay();
      const diff = (day - weekStartsOn + 7) % 7;
      const thisWeekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
      const lastWeekStart = new Date(thisWeekStart.getFullYear(), thisWeekStart.getMonth(), thisWeekStart.getDate() - 7);
      const lastWeekEnd = new Date(thisWeekStart.getFullYear(), thisWeekStart.getMonth(), thisWeekStart.getDate() - 1);
      return { after: `${localDate(lastWeekStart)}T00:00:00`, before: `${localDate(lastWeekEnd)}T23:59:59`, interval: 'day' };
    }
    case 'thisMonth': {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return { after: `${localDate(s)}T00:00:00`, before: endOfToday, interval: 'day' };
    }
    case 'lastMonth': {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      return { after: `${localDate(s)}T00:00:00`, before: `${localDate(e)}T23:59:59`, interval: 'day' };
    }
    case 'thisQuarter': {
      const qMonth = Math.floor(now.getMonth() / 3) * 3;
      const s = new Date(now.getFullYear(), qMonth, 1);
      return { after: `${localDate(s)}T00:00:00`, before: endOfToday, interval: 'week' };
    }
    case 'thisYear': {
      return { after: `${now.getFullYear()}-01-01T00:00:00`, before: endOfToday, interval: 'month' };
    }
    default: {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
      return { after: `${localDate(d)}T00:00:00`, before: endOfToday, interval: 'day' };
    }
  }
}
